import { styled, CSSObject, Theme, alpha } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import ListItemButton from '@mui/material/ListItemButton';
import COLORS from '../../../constants/colors';
import { APP_HEADER_HEIGHT } from '../../../constants/layout';

export const drawerWidth = 264;
/** Collapsed rail width: wide enough for the logo mark and the toggle beneath it. */
export const railWidth = 96;
/** Matches the navbar so the two bottom borders read as one line. */
export const sidebarHeaderHeight = APP_HEADER_HEIGHT;

/**
 * Sidebar palette. Derived from the brand tokens so the menu stays on-brand.
 * The active accent is the brand blue: COLORS.PRIMARY_BLUE is used for the tint,
 * the indicator and the focus ring, but at 4.2:1 on white it just misses WCAG AA
 * for 14px text, so labels and icons use a slightly deepened shade of it instead.
 */
export const SIDEBAR = {
  surface: COLORS.WHITE,
  border: alpha(COLORS.MAIN_DARK, 0.08),
  idleText: COLORS.DARK_GREY,
  idleIcon: alpha(COLORS.MAIN_DARK, 0.55),
  hoverSurface: alpha(COLORS.MAIN_DARK, 0.04),
  activeText: '#1E72A8',
  activeSurface: alpha(COLORS.PRIMARY_BLUE, 0.12),
  activeSurfaceHover: alpha(COLORS.PRIMARY_BLUE, 0.2),
  activeIndicator: COLORS.PRIMARY_BLUE,
  muted: COLORS.LIGHT_GRAY,
};

const surfaceMixin = (): CSSObject => ({
  borderWidth: 0,
  borderRight: `1px solid ${SIDEBAR.border}`,
  backgroundColor: SIDEBAR.surface,
  overflowX: 'hidden',
});

export const openedMixin = (theme: Theme): CSSObject => ({
  ...surfaceMixin(),
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

export const closedMixin = (theme: Theme): CSSObject => ({
  ...surfaceMixin(),
  width: railWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
});

export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex !important',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  height: APP_HEADER_HEIGHT,
  minHeight: APP_HEADER_HEIGHT,
}));

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop: string) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  maxHeight: '100vh',
  overflow: 'hidden',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

/**
 * One navigation row. States are expressed through colour + weight + a 3px
 * indicator rather than a solid fill, so a long menu reads as a list instead of
 * a stack of blocks. `data-depth="1"` marks a nested child, `data-section`
 * marks a collapsed parent that owns the active route.
 */
export const CustomListItemButton = styled(ListItemButton)(({ theme }) => ({
  position: 'relative',
  minHeight: '40px',
  padding: '8px 12px',
  borderRadius: '8px',
  color: SIDEBAR.idleText,
  backgroundColor: 'transparent',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiListItemIcon-root': {
    color: SIDEBAR.idleIcon,
    transition: theme.transitions.create('color', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
  '&:hover': {
    backgroundColor: SIDEBAR.hoverSurface,
    color: COLORS.MAIN_DARK,
    '& .MuiListItemIcon-root': {
      color: COLORS.MAIN_DARK,
    },
  },
  '&.Mui-focusVisible': {
    backgroundColor: SIDEBAR.hoverSurface,
    outline: `2px solid ${COLORS.PRIMARY_BLUE}`,
    outlineOffset: '-2px',
  },
  // Collapsed parent that owns the current route: emphasised, but not "selected".
  '&[data-section="active"]': {
    color: SIDEBAR.activeText,
    '& .MuiListItemText-primary': {
      fontWeight: 600,
    },
    '& .MuiListItemIcon-root': {
      color: SIDEBAR.activeText,
    },
  },
  '&.Mui-selected': {
    color: SIDEBAR.activeText,
    backgroundColor: SIDEBAR.activeSurface,
    '& .MuiListItemText-primary': {
      fontWeight: 600,
    },
    '& .MuiListItemIcon-root': {
      color: SIDEBAR.activeText,
    },
    '&:hover': {
      backgroundColor: SIDEBAR.activeSurfaceHover,
      color: SIDEBAR.activeText,
      '& .MuiListItemIcon-root': {
        color: SIDEBAR.activeText,
      },
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: '3px',
      height: '20px',
      borderRadius: '0 3px 3px 0',
      backgroundColor: SIDEBAR.activeIndicator,
    },
  },
  '&[data-depth="1"]': {
    minHeight: '36px',
    '& .MuiListItemText-primary': {
      fontWeight: 400,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
    '&.Mui-selected .MuiListItemText-primary': {
      fontWeight: 600,
    },
  },
}));

/** Thin, hover-revealed scrollbar for the scrolling nav region. */
export const scrollAreaSx = {
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'thin',
  scrollbarColor: `${alpha(COLORS.MAIN_DARK, 0.18)} transparent`,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'transparent',
    borderRadius: '3px',
  },
  '&:hover::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(COLORS.MAIN_DARK, 0.18),
  },
} as const;
